import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { presetsApi, GENRES } from "../../api/presets";

const labelClass = "font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2";
const inputClass =
  "w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none transition-colors";

// The native <input type="file"> is hidden rather than styled. Chrome draws a
// thumbnail placeholder -- a white rounded square -- inside the control next to
// the filename, and it lives in the shadow DOM where ::file-selector-button
// cannot reach it. The only reliable way to not have it is to not render a
// visible file input. The label below is the whole control; the input stays in
// the DOM (sr-only, not display:none) so it keeps focus and keyboard access.
function FilePicker({ id, accept, file, onPick }) {
  return (
    <div className="flex items-stretch overflow-hidden rounded border border-white/10 bg-[#1a1a1e] transition-colors focus-within:border-[#FF5500]">
      <input
        id={id}
        type="file"
        accept={accept}
        onChange={(e) => onPick(e.target.files[0] || null)}
        className="sr-only peer"
      />
      <label
        htmlFor={id}
        className="cursor-pointer bg-[#FF5500] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7733] peer-focus-visible:ring-2 peer-focus-visible:ring-white/60"
      >
        Choose file
      </label>
      {/* min-w-0 + truncate so a long filename shortens instead of stretching
          the row past the form. */}
      <span className={`min-w-0 flex-1 self-center truncate px-4 text-sm ${file ? "text-white" : "text-[#71717A]"}`}>
        {file ? file.name : "No file selected"}
      </span>
    </div>
  );
}

export default function KitUpload() {
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [pattern, setPattern] = useState(null);
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState(GENRES[0]);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!kit || !pattern || !title.trim()) {
      setError("Kit, pattern and title are required.");
      return;
    }
    const fd = new FormData();
    fd.append("kit", kit);
    fd.append("pattern", pattern);
    fd.append("title", title.trim());
    fd.append("genre", genre);
    fd.append("description", description.trim());
    setBusy(true);
    setProgress(0);
    try {
      const r = await presetsApi.upload(fd, (evt) =>
        setProgress(Math.round((evt.loaded / (evt.total || 1)) * 100))
      );
      const newId = r?.data?.id;
      navigate(newId ? `/kits/${newId}` : "/kits");
    } catch (err) {
      // The backend returns meaningful 422/409/413/429/503 messages in
      // `detail` (bad file, duplicate, too large, rate-limited, storage
      // unavailable) — surface it rather than a generic failure.
      setError(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto max-w-xl px-6 py-12 sm:py-16">
        <h1 className="font-clash text-3xl font-bold sm:text-4xl mb-8">Share a kit + pattern</h1>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className={labelClass} htmlFor="kit-file">Kit file (.tmkit)</label>
            <FilePicker id="kit-file" accept=".tmkit" file={kit} onPick={setKit} />
          </div>
          <div>
            <label className={labelClass} htmlFor="pattern-file">Pattern file (.tmpat)</label>
            <FilePicker id="pattern-file" accept=".tmpat" file={pattern} onPick={setPattern} />
          </div>
          <div>
            <label className={labelClass}>Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Genre</label>
            <select value={genre} onChange={(e) => setGenre(e.target.value)} className={inputClass}>
              {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              className={inputClass}
            />
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}
          {busy && <p className="text-[#A1A1AA] text-sm">Uploading {progress}%</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full py-3 rounded text-sm font-semibold disabled:opacity-50">
            Share
          </button>
        </form>
      </div>
    </div>
  );
}
